import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import { useJobRunsByType } from '@Hooks/useJobRuns';

type FlowFinding = {
  flowTitle: string | null;
  stageTitle: string;
  populationP: number;
  signalB: number;
  lapsed: number;
  skippedReason?: string;
  summary?: string;
};

type LapseReport = {
  runAt?: string;
  stagesScanned?: number;
  flowsScanned?: number;
  lapsedEmitted?: number;
  skipped?: number;
  flows?: FlowFinding[];
};

function Node() {
  return (
    <span className="absolute top-4 left-[9px] z-10 flex h-3 w-3 -translate-x-1/2 items-center justify-center">
      <span className="relative h-2.5 w-2.5 rounded-full bg-zinc-300 ring-2 ring-white" />
    </span>
  );
}

function RunCard({ report, createdAt, status }: { report: LapseReport; createdAt: string; status: string }) {
  const when = report.runAt ?? createdAt;
  const flows = report.flows ?? [];
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-zinc-800">
          {report.lapsedEmitted ?? 0} lapse event{(report.lapsedEmitted ?? 0) === 1 ? '' : 's'}
          <span className="text-zinc-400"> · {report.flowsScanned ?? 0} flow(s)</span>
        </span>
        <span className="text-xs text-zinc-400">
          {new Date(when).toLocaleString()}
          {status === 'FAILURE' && <span className="ml-2 text-red-500">failed</span>}
        </span>
      </div>
      {flows.length > 0 && (
        <ul className="mt-3 space-y-2 text-xs text-zinc-600">
          {flows.map((f, i) => (
            <li key={i} className="space-y-0.5">
              <div className="flex justify-between gap-3">
                <span className="truncate font-medium text-zinc-700">
                  {f.flowTitle ?? 'Flow'} — {f.stageTitle}
                </span>
                <span className="shrink-0 text-zinc-500">
                  {f.skippedReason ? (
                    <em className="text-amber-600">skipped</em>
                  ) : (
                    <>
                      lapsed {f.lapsed} <span className="text-zinc-400">(P {f.populationP} · B {f.signalB})</span>
                    </>
                  )}
                </span>
              </div>
              {f.summary && <p className="italic text-zinc-500">{f.summary}</p>}
              {f.skippedReason && <p className="text-amber-600/80">{f.skippedReason}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function LapseDetectionFeed() {
  const { data, status } = useJobRunsByType('detect-lapses');
  const runs = [...(data?.data ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  if (status === 'pending') {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute top-3 bottom-3 left-[9px] w-px bg-zinc-200" />
      <div className="space-y-3">
        {!runs.length ? (
          <div className="relative pl-7">
            <Node />
            <div className="rounded-xl border border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-500">
              No sweeps recorded yet.
            </div>
          </div>
        ) : (
          runs.map((run, idx) => (
            <div key={run.id} className="agent-thought-in relative pl-7" style={{ animationDelay: `${idx * 70}ms` }}>
              <Node />
              <RunCard report={(run.results ?? {}) as LapseReport} createdAt={run.createdAt} status={run.status} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
